import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';

const IMG = `${process.env.PUBLIC_URL}/images`;
const PIMG = `${IMG}/Philosophy`;

const philosophyTraits = [
  { icon: 'users', title: 'People First', desc: 'We put people at the center of every decision and innovation.', color: '#ec4899' },
  { icon: 'target', title: 'Purpose Driven', desc: 'We are driven by purpose and committed to creating real impact.', color: '#38bdf8' },
  { icon: 'handshake', title: 'Built on Trust', desc: 'We build with integrity, transparency, and mutual respect.', color: '#22c55e' },
  { icon: 'trending-up', title: 'Future Ready', desc: 'We embrace change, learn continuously, and build for the future.', color: '#f59e0b' },
];

const coreValues = [
  {
    image: `${PIMG}/Philosophy-6.jpeg`,
    icon: 'users',
    title: 'Collaboration',
    desc: 'We believe in the power of teamwork and diverse perspectives to achieve extraordinary outcomes.',
  },
  {
    image: `${PIMG}/Philosophy-2.jpeg`,
    icon: 'shield-check',
    title: 'Integrity',
    desc: 'We do what’s right, even when no one is watching. Honesty is our foundation.',
  },
  {
    image: `${PIMG}/Philosophy-3.jpeg`,
    icon: 'lightbulb',
    title: 'Innovation',
    desc: 'We challenge the status quo and embrace creativity to build intelligent ecosystems that transform lives.',
  },
  {
    image: `${PIMG}/Philosophy-4.jpeg`,
    icon: 'leaf',
    title: 'Sustainability',
    desc: 'We are committed to building responsible businesses that create long-term value.',
  },
  {
    image: `${PIMG}/Philosophy-5.jpeg`,
    icon: 'heart',
    title: 'Excellence',
    desc: 'We strive for excellence in everything we do, delivering quality that creates trust and joy.',
  },
];

const principles = [
  {
    title: 'Customer Obsession',
    desc: 'We deeply understand our customers and are relentless in creating value for them.',
  },
  {
    title: 'Empowerment',
    desc: 'We empower our people to think big, take ownership, and make a difference.',
  },
  {
    title: 'Agility',
    desc: 'We adapt quickly, make informed decisions and turn challenges into opportunities.',
  },
  {
    title: 'Learning Mindset',
    desc: 'We are curious, open to ideas, and committed to continuous growth.',
  },
  {
    title: 'Inclusive Culture',
    desc: 'We celebrate diversity and foster an inclusive environment where everyone belongs.',
  },
];

const purposeCards = [
  { image: `${IMG}/careers/careers-6.jpeg`, title: 'Empowering People' },
  { image: `${PIMG}/Philosophy-4.jpeg`, title: 'Sustainable Future' },
  { image: `${IMG}/careers/careers-4.jpeg`, title: 'Innovative Solutions' },
  { image: `${IMG}/careers/careers-5.jpeg`, title: 'Creating Joy' },
];

export default function Philosophy() {
  return (
    <div className="ph-page">
      {/* =================== HERO =================== */}
      <section className="ph-hero">
        <div
          className="ph-hero__bg"
          style={{ backgroundImage: `url(${PIMG}/Philosophy-1.jpeg)` }}
        />
        <div className="container container--wide ph-hero__inner">
          <div className="crumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/about">About Us</Link>
            <span>/</span>
            <span>Our Philosophy</span>
          </div>

          <div className="ph-hero__copy">
            <h1 className="ph-hero__title">
              Our Philosophy.
              <br />
              The Foundation of
              <br />
              <span className="grad-pink">Everything We Do.</span>
            </h1>
            <span className="ph-hero__rule" />
            <p className="ph-hero__lead">
              At Caladium Systems, our philosophy is the compass that guides our
              decisions, shapes our culture, and drives our pursuit of spreading
              joy.
            </p>
            <Link to="#core" className="btn btn--outline">
              Discover Our Philosophy <Icon name="arrow-right" size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* =================== OUR PHILOSOPHY PANEL =================== */}
      <section className="ph-philosophy-sec">
        <div className="container container--wide">
          <div className="ph-philosophy">
            <div className="ph-philosophy__lead">
              <div className="ph-lotus">
                <img
                  src={`${IMG}/Logo-caladium.png`}
                  alt="Caladium"
                />
              </div>
              <span className="eyebrow">OUR PHILOSOPHY</span>
              <h2 className="ph-philosophy__title">Spreading Joy</h2>
              <p>
                We believe joy is a powerful force&mdash;it connects people,
                fuels purpose, and creates a positive ripple effect in the
                world. Everything we build, partner with, and do is designed to
                spread joy and create meaningful, lasting impact.
              </p>
            </div>
            <div className="ph-philosophy__traits">
              {philosophyTraits.map((t) => (
                <div className="ph-trait" key={t.title}>
                  <span
                    className="ph-trait__icon"
                    style={{ color: t.color, borderColor: t.color }}
                  >
                    <Icon name={t.icon} size={28} />
                  </span>
                  <h3>{t.title}</h3>
                  <p>{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== CORE VALUES =================== */}
      <section className="ph-values-sec" id="core">
        <div className="container container--wide">
          <header className="section__head ph-head">
            <span className="eyebrow">OUR CORE VALUES</span>
            <h2 className="h2">Values That Define Us</h2>
          </header>

          <div className="ph-values">
            {coreValues.map((v) => (
              <article className="ph-value" key={v.title}>
                <div className="ph-value__media">
                  <img src={v.image} alt={v.title} />
                  <span className="ph-value__veil" />
                </div>
                <span className="ph-value__icon">
                  <Icon name={v.icon} size={26} />
                </span>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =================== GUIDING PRINCIPLES =================== */}
      <section className="ph-principles-sec">
        <div className="container container--wide">
          <header className="section__head ph-head">
            <span className="eyebrow">OUR GUIDING PRINCIPLES</span>
            <h2 className="h2">How We Work, Every Day</h2>
          </header>

          <div className="ph-principles">
            {principles.map((p) => (
              <div className="ph-principle" key={p.title}>
                <span className="ph-principle__check">
                  <Icon name="shield-check" size={20} />
                </span>
                <div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== PURPOSE IN ACTION =================== */}
      <section className="ph-purpose-sec">
        <div className="container container--wide">
          <div className="ph-purpose">
            <div className="ph-purpose__copy">
              <span className="eyebrow">OUR PURPOSE IN ACTION</span>
              <h2 className="h2">
                Building Ecosystems
                <br />
                That <span className="grad-pink">Spread Joy</span>
              </h2>
              <p>
                From the technologies we build to the brands we partner with,
                our purpose remains the same &mdash; to enrich lives, empower
                communities, and create a better tomorrow.
              </p>
              <Link to="/impact" className="btn btn--outline">
                Explore Our Impact <Icon name="arrow-right" size={16} />
              </Link>
            </div>
            <div className="ph-purpose__grid">
              {purposeCards.map((c) => (
                <div className="ph-purpose__card" key={c.title}>
                  <img src={c.image} alt={c.title} />
                  <span className="ph-purpose__veil" />
                  <span className="ph-purpose__title">{c.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== CLOSING QUOTE =================== */}
      <section className="ph-quote-sec">
        <div className="container container--wide">
          <div className="ph-quote">
            <span className="ph-quote__mark">
              <Icon name="quote" size={40} />
            </span>
            <p className="ph-quote__text">
              We don&rsquo;t just build businesses.
              <br />
              We build a movement of joy, purpose and impact.
            </p>
            <div className="ph-quote__by">
              <img
                src={`${IMG}/Caladium_MD.png`}
                alt="Pradhyumna T Venkat"
                className="ph-quote__photo"
              />
              <div>
                <strong>Pradhyumna T Venkat</strong>
                <span>Founder &amp; CEO, Caladium Systems</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
